import { useMemo } from 'react'

import { filterTokens } from '@/components/SearchModal/filtering'
import { useTokenComparator } from '@/components/SearchModal/sorting'
import { getNativeCurrency, isNativeCurrencySymbol } from '@/config/native'
import { useToken } from '@/hooks/Tokens'
import { ChainId, Currency, Token } from '@/sdk'
import { isAddress } from '@/utils'

import { resolveImportableAddressSearch } from './importableTokenSearch'

function matchesNativeSearch(chainId: ChainId, search: string): boolean {
  const q = search.trim().toLowerCase()
  if (!q) return true
  const native = getNativeCurrency(chainId)
  return (
    native.symbol?.toLowerCase().includes(q) ||
    native.name?.toLowerCase().includes(q) ||
    isNativeCurrencySymbol(q, chainId)
  )
}

/** Búsqueda unificada de tokens: símbolo, nombre o contrato on-chain. */
export function useImportableTokenSearchList(
  chainId: ChainId,
  search: string,
  selectableTokens: readonly Currency[],
) {
  const isAddressSearch = isAddress(search)
  const searchToken = useToken(isAddressSearch ? search : undefined)
  const tokenComparator = useTokenComparator(false)

  const tokens = useMemo(() => {
    if (isAddressSearch) {
      if (searchToken === null) return []
      if (searchToken instanceof Token) {
        const known = selectableTokens.some(
          (currency) =>
            currency instanceof Token &&
            currency.address.toLowerCase() === searchToken.address.toLowerCase(),
        )
        return known ? [searchToken] : []
      }
      return []
    }

    const native = selectableTokens.find((currency) => !(currency instanceof Token))
    const erc20 = selectableTokens.filter((currency): currency is Token => currency instanceof Token)
    const filtered = filterTokens(erc20, search).sort(tokenComparator)
    const includeNative = native != null && matchesNativeSearch(chainId, search)
    return includeNative ? [native, ...filtered] : filtered
  }, [chainId, isAddressSearch, search, searchToken, selectableTokens, tokenComparator])

  const addressSearch = useMemo(
    () => resolveImportableAddressSearch(search, searchToken, selectableTokens),
    [search, searchToken, selectableTokens],
  )

  return {
    tokens,
    searchToken,
    ...addressSearch,
  }
}
